import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';

import { TooltipButtonComponent } from '@app/core/components/tooltip-button/tooltip-button.component';
import { GuidedCourseCreationService } from '@services/guided-course-creation.service';
import { LoadingSpinnerComponent } from '@shared/loading-spinner/loading-spinner.component';
import { SuggestionChipComponent } from '@shared/suggestion-chip/suggestion-chip.component';
import {
  DOCUMENT_ERROR_KEYS,
  DOCUMENT_MODES,
  DOCUMENT_UPLOAD,
  DOCUMENT_UPLOAD_STATES,
  DocumentMode,
  DocumentUploadResponse,
  DocumentUploadState,
  DocumentUploadStatus,
  getDocumentErrorKey,
  getDocumentRejectionKey,
  mergeSuggestedTopics,
  validateDocumentFile,
} from '@core/constants/guided-course-creation.constants';

interface TopicChip {
  topic: string;
  used: boolean;
}

export interface DocumentEntry {
  readonly id: number;
  readonly file: File;
  readonly documentId: string | null;
  readonly state: DocumentUploadState;
  readonly pageCount: number | null;
  readonly suggestedTopics: readonly string[];
  readonly errorKey: string | null;
  readonly isRemoving: boolean;
}

@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatSlideToggleModule,
    TranslateModule,
    TooltipButtonComponent,
    LoadingSpinnerComponent,
    SuggestionChipComponent,
  ],
})
export class DocumentUploadComponent {
  readonly acceptedFileTypes = DOCUMENT_UPLOAD.ACCEPTED_FILE_TYPES;
  readonly uploadStates = DOCUMENT_UPLOAD_STATES;
  readonly messageParams = {
    size: DOCUMENT_UPLOAD.MAX_SIZE_LABEL,
    max: DOCUMENT_UPLOAD.MAX_DOCUMENTS,
  };

  readonly sessionId = input.required<string | null>();
  readonly disabled = input(false);
  readonly currentTopic = input('');

  readonly statusChange = output<DocumentUploadStatus>();
  readonly topicSelected = output<string>();

  readonly entries = signal<readonly DocumentEntry[]>([]);
  readonly documentMode = signal<DocumentMode>(DOCUMENT_MODES.RELAXED);

  readonly isUploading = computed(() =>
    this.entries().some(
      entry => entry.state === DOCUMENT_UPLOAD_STATES.UPLOADING,
    ),
  );

  // Rejected files never reached the session, so they must not consume a slot.
  readonly activeCount = computed(
    () =>
      this.entries().filter(
        entry => entry.state !== DOCUMENT_UPLOAD_STATES.ERROR,
      ).length,
  );

  readonly isUploadDisabled = computed(
    () =>
      this.disabled() ||
      this.isUploading() ||
      this.activeCount() >= DOCUMENT_UPLOAD.MAX_DOCUMENTS,
  );

  readonly suggestedTopics = computed(() =>
    mergeSuggestedTopics(this.entries().map(entry => entry.suggestedTopics)),
  );

  readonly showModeToggle = computed(() =>
    this.entries().some(entry => entry.state === DOCUMENT_UPLOAD_STATES.DONE),
  );

  readonly isStrictMode = computed(
    () => this.documentMode() === DOCUMENT_MODES.STRICT,
  );

  readonly modeDescriptionKey = computed(
    () => `guidedCourseCreation.document.mode.${this.documentMode()}`,
  );

  readonly topicChips = computed<readonly TopicChip[]>(() =>
    this.suggestedTopics().map(topic => ({
      topic,
      used: this.currentTopic().includes(topic),
    })),
  );

  private readonly fileInput =
    viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  private readonly gccService = inject(GuidedCourseCreationService);
  private readonly destroyRef = inject(DestroyRef);

  private uploadSubscription: Subscription | null = null;
  private nextEntryId = 0;

  // UPLOADING wins so the parent keeps blocking, then DONE: one good file is enough.
  private readonly aggregateState = computed<DocumentUploadState>(() => {
    const entries = this.entries();

    if (!entries.length) {
      return DOCUMENT_UPLOAD_STATES.IDLE;
    }
    if (this.isUploading()) {
      return DOCUMENT_UPLOAD_STATES.UPLOADING;
    }

    return entries.some(entry => entry.state === DOCUMENT_UPLOAD_STATES.DONE)
      ? DOCUMENT_UPLOAD_STATES.DONE
      : DOCUMENT_UPLOAD_STATES.ERROR;
  });

  openFilePicker(): void {
    this.fileInput().nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const [file] = (event.target as HTMLInputElement).files ?? [];

    const sessionId = this.sessionId();
    if (!file || !sessionId || this.isUploadDisabled()) {
      return;
    }

    // Cleared right away so the same file can be picked again after a removal.
    this.fileInput().nativeElement.value = '';

    const rejectionReason = validateDocumentFile(file);
    if (rejectionReason) {
      this.addEntry(file, {
        state: DOCUMENT_UPLOAD_STATES.ERROR,
        errorKey: getDocumentRejectionKey(rejectionReason),
      });
      this.emitStatus();
      return;
    }

    this.startUpload(sessionId, file);
  }

  onSelectTopic(topic: string): void {
    this.topicSelected.emit(topic);
  }

  onModeChange(strict: boolean): void {
    this.documentMode.set(
      strict ? DOCUMENT_MODES.STRICT : DOCUMENT_MODES.RELAXED,
    );
    this.emitStatus();
  }

  onRemove(entry: DocumentEntry): void {
    const sessionId = this.sessionId();

    if (!sessionId || !entry.documentId) {
      this.dropEntry(entry.id);
      return;
    }

    this.patchEntry(entry.id, { isRemoving: true, errorKey: null });

    this.gccService
      .deleteDocument(sessionId, entry.documentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dropEntry(entry.id),
        error: () =>
          this.patchEntry(entry.id, {
            isRemoving: false,
            errorKey: DOCUMENT_ERROR_KEYS.REMOVE_FAILED,
          }),
      });
  }

  private startUpload(sessionId: string, file: File): void {
    const entryId = this.addEntry(file, {
      state: DOCUMENT_UPLOAD_STATES.UPLOADING,
    });
    this.emitStatus();

    this.uploadSubscription = this.gccService
      .uploadDocument(sessionId, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.handleAnalysis(entryId, response),
        error: (error: HttpErrorResponse) =>
          this.handleHttpError(entryId, error),
      });
  }

  private addEntry(file: File, patch: Partial<DocumentEntry>): number {
    const id = this.nextEntryId++;

    this.entries.update(entries => [
      ...entries,
      {
        id,
        file,
        documentId: null,
        state: DOCUMENT_UPLOAD_STATES.UPLOADING,
        pageCount: null,
        suggestedTopics: [],
        errorKey: null,
        isRemoving: false,
        ...patch,
      },
    ]);

    return id;
  }

  private patchEntry(id: number, patch: Partial<DocumentEntry>): void {
    this.entries.update(entries =>
      entries.map(entry => (entry.id === id ? { ...entry, ...patch } : entry)),
    );
  }

  private dropEntry(id: number): void {
    const entry = this.entries().find(candidate => candidate.id === id);
    if (entry?.state === DOCUMENT_UPLOAD_STATES.UPLOADING) {
      this.uploadSubscription?.unsubscribe();
    }

    this.entries.update(entries =>
      entries.filter(candidate => candidate.id !== id),
    );

    if (!this.entries().length) {
      this.documentMode.set(DOCUMENT_MODES.RELAXED);
    }

    this.emitStatus();
  }

  private emitStatus(): void {
    this.statusChange.emit({
      state: this.aggregateState(),
      suggestedTopics: this.suggestedTopics(),
      documentMode: this.documentMode(),
    });
  }

  private handleAnalysis(id: number, response: DocumentUploadResponse): void {
    this.patchEntry(id, {
      state: DOCUMENT_UPLOAD_STATES.DONE,
      documentId: response.id,
      pageCount: response.analysis?.page_count ?? null,
      suggestedTopics: response.analysis?.suggested_topics ?? [],
    });
    this.emitStatus();
  }

  private handleHttpError(id: number, error: HttpErrorResponse): void {
    this.patchEntry(id, {
      state: DOCUMENT_UPLOAD_STATES.ERROR,
      errorKey: getDocumentErrorKey(error),
    });
    this.emitStatus();
  }
}
